/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Popover,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

import { HexColorPicker } from 'react-colorful';
import { isValidHex, getContrastRatio } from '../../utils/colorUtils';

/**
 * Props for the ColorPicker component
 */
interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  helperText?: string;
  disabled?: boolean;
  contrastWith?: string; // Color to check contrast against
  showPalette?: boolean;
  error?: boolean;
  required?: boolean;
}

/**
 * Professional color picker component with hex input, visual picker, and accessibility validation
 */
const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  helperText,
  disabled = false,
  contrastWith,
  showPalette = true,
  error = false,
  required = false,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const [isValidColor, setIsValidColor] = useState(true);
  const [contrastRatio, setContrastRatio] = useState<number | null>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    // Strip leading # for display (we add it back via InputProps.startAdornment)
    const displayValue = value.startsWith('#') ? value.slice(1) : value;
    setInputValue(displayValue);
    setIsValidColor(isValidHex(value));
  }, [value]);

  // Calculate contrast ratio when colors change
  useEffect(() => {
    if (contrastWith && isValidHex(value) && isValidHex(contrastWith)) {
      const ratio = getContrastRatio(value, contrastWith);
      setContrastRatio(ratio);
    } else {
      setContrastRatio(null);
    }
  }, [value, contrastWith]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    // Add # prefix for validation and callback
    const hexValue = newValue.startsWith('#') ? newValue : `#${newValue}`;

    if (isValidHex(hexValue)) {
      setIsValidColor(true);
      onChange(hexValue);
    } else {
      setIsValidColor(false);
    }
  };

  const handlePickerChange = (color: string) => {
    // Strip # for display
    const displayValue = color.startsWith('#') ? color.slice(1) : color;
    setInputValue(displayValue);
    setIsValidColor(true);
    onChange(color);
  };

  const handlePickerOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePickerClose = () => {
    setAnchorEl(null);
  };

  const getContrastStatus = () => {
    if (!contrastRatio) return null;

    if (contrastRatio >= 7) {
      return { icon: CheckCircleIcon, color: 'success', text: 'AAA' };
    }
    if (contrastRatio >= 4.5) {
      return { icon: CheckCircleIcon, color: 'success', text: 'AA' };
    }
    if (contrastRatio >= 3) {
      return { icon: WarningIcon, color: 'warning', text: 'Poor' };
    }
    return { icon: ErrorIcon, color: 'error', text: 'Fail' };
  };

  const contrastStatus = getContrastStatus();

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {label}
          {required && (
            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>

        {/* Contrast ratio indicator */}
        {contrastStatus && (
          <Tooltip
            title={`Contrast ratio: ${contrastRatio?.toFixed(2)}:1 (${contrastStatus.text})`}
            arrow
          >
            <Chip
              icon={<contrastStatus.icon fontSize="small" />}
              label={contrastStatus.text}
              size="small"
              color={contrastStatus.color as any}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.75rem' }}
            />
          </Tooltip>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Color swatch and picker trigger */}
        <Tooltip title="Open color picker" arrow>
          <IconButton
            onClick={handlePickerOpen}
            disabled={disabled}
            sx={{
              width: 40,
              height: 40,
              border: '2px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
              background: isValidColor ? value : 'transparent',
              backgroundImage: !isValidColor
                ? 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)'
                : 'none',
              backgroundSize: '8px 8px',
              backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              '&:hover': {
                borderColor: theme.palette.primary.main,
              },
              position: 'relative',
            }}
          >
            {!isValidColor && (
              <PaletteIcon
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: 20,
                }}
              />
            )}
          </IconButton>
        </Tooltip>

        {/* Hex input field */}
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          placeholder="000000"
          size="small"
          disabled={disabled}
          error={error || !isValidColor}
          helperText={
            !isValidColor ? 'Invalid hex color (e.g., FF5733)' : helperText
          }
          sx={{
            flex: 1,
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem',
            },
          }}
          InputProps={{
            startAdornment: (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: 'monospace',
                  mr: 0.5,
                }}
              >
                #
              </Typography>
            ),
          }}
        />
      </Box>

      {/* Color picker popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2 }}>
          <HexColorPicker
            color={isValidColor ? value : '#000000'}
            onChange={handlePickerChange}
          />

          {/* Quick color presets */}
          {showPalette && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                Quick colors
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {[
                  '#1976d2', // Material-UI primary
                  '#dc004e', // Material-UI secondary
                  '#4CAF50', // Success green
                  '#FF9800', // Warning orange
                  '#F44336', // Error red
                  '#2196F3', // Blue
                  '#9C27B0', // Purple
                  '#795548', // Brown
                  '#607D8B', // Blue grey
                  '#000000', // Black
                  '#FFFFFF', // White
                  '#9E9E9E', // Grey
                ].map(color => (
                  <Box
                    key={color}
                    onClick={() => handlePickerChange(color)}
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export default ColorPicker;
